import { combineReducers } from '@reduxjs/toolkit';
import localForage from 'localforage';
import { PersistConfig, persistReducer } from 'redux-persist';
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';

// Data Layer reducers
import { networkStatusReducer } from 'data-layer-cr/dist/network';
import { carPoliciesReducer } from 'data-layer-cr/dist/slices/carPolicies';
import { CarPoliciesSliceState } from 'data-layer-cr/dist/slices/carPolicies/slice/carPoliciesSlice';
import paymentErrorReducer, {
  PaymentErrorState,
} from 'data-layer-cr/dist/slices/paymentError/slice';

// Models
import ACHData from '../models/helperTypes/ACHData';
import ClaveData from '../models/helperTypes/ClaveData';

// Local project reducers
import achDataReducer from './achData/slice';
import carCatalogReducer from './carCatalog/slice';
import claveDataReducer from './claveData/slice';
import coverageCatalogReducer from './coverageCatalog/slice';
import creditorCatalogReducer, {
  CreditorCatalogState,
} from './creditorCatalog/slice';
import emissionPolicyReducer, {
  EmissionPolicyState,
} from './emissionPolicy/slice';
import inspectionFormReducer, {
  InspectionFormState,
} from './inspectionForm/slice';
import legalEntityCatalogReducer, {
  LegalEntityCatalogSliceState,
} from './legalEntityCatalog/slice';
import personReducer from './person/slice';
import personValidateReducer, {
  PersonValidateDataState,
} from './person/slice/personValidateSlice';
import shareholderValidateReducer, {
  ShareholderValidateDataState,
} from './person/slice/shareholderValidateSlice';
import profileReducer, { ProfileState } from './profile/slice/profileSlice';
import quotationReducer, {
  QuotationState,
} from './quotation/slice/quotationSlice';
import signatureReducer from './signature/slice';
import { SignatureState } from './signature/slice/signatureSlice';
import steppersSliceReducer, { StepperState } from './steppers/slice';

// Extra
import creditCardDataSlice from './creditCardData/slice';
import { PersonDataState } from './person/slice/personSlice';

/**
 * Schema describing the "persist" logic for the profile information.
 * Since the "emission" process can outlast the session, and we don't want the app to forget
 * the role in case there is a refresh, we need to add it to redux persist.
 */
const profilePersist: PersistConfig<ProfileState> = {
  key: 'profile',
  storage: localForage,
  whitelist: ['currentRole', 'profile', 'currentLicense'],
  stateReconciler: autoMergeLevel2,
};

const paymentErrorPersist: PersistConfig<PaymentErrorState> = {
  key: 'paymentError',
  storage: localForage,
  whitelist: ['code', 'description'],
  stateReconciler: autoMergeLevel2,
};

/** Schema describing the "persist" logic on the inspection form */
const inspectionFormPersist: PersistConfig<InspectionFormState> = {
  key: 'inspectionForm',
  storage: localForage,
  whitelist: [
    'carImages',
    'uploadStateMap',
    'blockSearchLicense',
    'vehicleRegistryFileName',
    'vehicleRegistryImage',
    'textValues',
  ],
  stateReconciler: autoMergeLevel2,
};

/** Schema describing the "persist" logic on the emission form */
const emissionFormPersist: PersistConfig<EmissionPolicyState> = {
  key: 'emisssionForm',
  storage: localForage,
  whitelist: [
    'dataFields',
    'finalBeneficiary',
    'identificationFileName',
    'identificationBase64',
    'paymentFields',
    'newCarInvoiceFilename',
    'newCarInvoiceBase64',
    'paymentIssuerSource',
    'paymentIssuer',
    'paymentTotal',
    'electronicPaymentTotal',
    'autoPxPaymentTotal',
    'discountedPaymentTotal',
    'additionalDocumentACHFileName',
    'additionalDocumentACHBase64',
  ],
  stateReconciler: autoMergeLevel2,
};

/** Schema describing the "persist" logic on the stepper */
const stepperPersist: PersistConfig<StepperState> = {
  key: 'stepper',
  storage: localForage,
  whitelist: [
    'isStartedInspection',
    'currentStepInspection',
    'isStartedEmision',
    'currentStepEmission',
    'isOnPackageSelection',
  ],
  stateReconciler: autoMergeLevel2,
};

/**
 * Schema describing the "persist" logic for the quotation reducer.
 * The "localSelectedPackageOption" is the only field in this slice we want to persist.
 */
const quotationPersist: PersistConfig<QuotationState> = {
  key: 'quotation',
  storage: localForage,
  stateReconciler: autoMergeLevel2,
};

/** Schema describing the "persist" logic for the car policies reducer */
const carPoliciesPersist: PersistConfig<CarPoliciesSliceState> = {
  key: 'carPolicies',
  storage: localForage,
  stateReconciler: autoMergeLevel2,
};

/** Schema describing the "persist" logic for the ach data reducer */
const achDataPersist: PersistConfig<ACHData> = {
  key: 'achData',
  storage: localForage,
  stateReconciler: autoMergeLevel2,
};

/** Schema describing the "persist" logic for the clave data reducer */
const claveDataPersist: PersistConfig<ClaveData> = {
  key: 'claveData',
  storage: localForage,
  stateReconciler: autoMergeLevel2,
};

const creditorCatalogPersist: PersistConfig<CreditorCatalogState> = {
  key: 'creditorCatalog',
  storage: localForage,
  stateReconciler: autoMergeLevel2,
};

const legalEntityCatalogPersist: PersistConfig<LegalEntityCatalogSliceState> = {
  key: 'legalEntityCatalog',
  storage: localForage,
  stateReconciler: autoMergeLevel2,
  whitelist: ['countriesMap', 'occupationsMap'],
};

const personPersist: PersistConfig<PersonDataState> = {
  key: 'personData',
  storage: localForage,
  stateReconciler: autoMergeLevel2,
  whitelist: [],
};

const personValidatePersist: PersistConfig<PersonValidateDataState> = {
  key: 'personValidateData',
  storage: localForage,
  stateReconciler: autoMergeLevel2,
  whitelist: [],
};

const shareholderValidatePersist: PersistConfig<ShareholderValidateDataState> =
  {
    key: 'shareholderValidateData',
    storage: localForage,
    stateReconciler: autoMergeLevel2,
    whitelist: [],
  };

const signaturePersist: PersistConfig<SignatureState> = {
  key: 'signature',
  storage: localForage,
  stateReconciler: autoMergeLevel2,
  whitelist: [],
};

const rootReducer = combineReducers({
  /** Register data layer reducers */
  networkStatus: networkStatusReducer,
  carPolicies: persistReducer<CarPoliciesSliceState>(
    carPoliciesPersist,
    carPoliciesReducer,
  ),
  paymentError: persistReducer<PaymentErrorState>(
    paymentErrorPersist,
    paymentErrorReducer,
  ),

  /**
   * Register local reducers. Note that slices that should be preserved between refreshes
   * should be registered with persistReducer.
   */
  quotation: persistReducer<QuotationState>(quotationPersist, quotationReducer),
  inspectionForm: persistReducer<InspectionFormState>(
    inspectionFormPersist,
    inspectionFormReducer,
  ),
  emissionPolicy: persistReducer<EmissionPolicyState>(
    emissionFormPersist,
    emissionPolicyReducer,
  ),
  stepper: persistReducer<StepperState>(stepperPersist, steppersSliceReducer),
  profile: persistReducer<ProfileState>(profilePersist, profileReducer),
  creditorCatalog: persistReducer<CreditorCatalogState>(
    creditorCatalogPersist,
    creditorCatalogReducer,
  ),
  achData: persistReducer<ACHData>(achDataPersist, achDataReducer),
  claveData: persistReducer<ClaveData>(claveDataPersist, claveDataReducer),
  coverageCatalog: coverageCatalogReducer,
  carCatalog: carCatalogReducer,
  person: persistReducer<PersonDataState>(personPersist, personReducer),
  personValidate: persistReducer<PersonValidateDataState>(
    personValidatePersist,
    personValidateReducer,
  ),
  shareholderValidate: persistReducer<ShareholderValidateDataState>(
    shareholderValidatePersist,
    shareholderValidateReducer,
  ),
  creditCardData: creditCardDataSlice,
  legalEntityCatalog: persistReducer<LegalEntityCatalogSliceState>(
    legalEntityCatalogPersist,
    legalEntityCatalogReducer,
  ),
  signature: persistReducer<SignatureState>(signaturePersist, signatureReducer),
});

export type RootState = ReturnType<typeof rootReducer>;

export default rootReducer;
